import React, { useEffect, useState, useRef, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, Polyline, Marker } from "react-leaflet";
import { renderToString } from "react-dom/server";
import { RiPlaneFill, RiFlightTakeoffFill, RiFlightLandFill, RiMapPin2Fill } from "@remixicon/react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { deriveQuoteStatus } from "@/features/track/utils/flightStatusUtils";

// FR24 direct client (uncomment if switching back from backend proxy)
// import Client from "@/features/flightradar/client";
// const client = new Client({ apiToken: process.env.REACT_APP_FR24_API_TOKEN, apiVersion: "v1" });

const CARTO_TILES = "https://basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
const CARTO_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>';

const INITIAL_CENTER = [20, 0]; // [lat, lng]
const INITIAL_ZOOM = 2;

// Adaptive polling intervals based on ETA remaining (minutes)
// > 30 min (cruising)   → 5 min
// 15–30 min (descending) → 2 min
// 5–15 min (approaching) → 30 s
// < 5 min (final approach) → 15 s
function getPollingInterval(etaMinutes) {
  if (etaMinutes === null || etaMinutes === undefined || etaMinutes > 30) return 5 * 60 * 1000;
  if (etaMinutes > 15) return 2 * 60 * 1000;
  if (etaMinutes > 5) return 30 * 1000;
  return 15 * 1000;
}

const PRE_FLIGHT_STATUSES = ["PENDING", "POSITIONING", "READY", "DELAYED"];

// Time-aware polling for ground (pre-flight) statuses.
// Returns ms to wait, OR 0 to signal "schedule a single timeout at T-45min".
function getGroundPollingInterval(dbFlight) {
  if (!dbFlight?.departure_date) return 5 * 60 * 1000;
  const status = (dbFlight.flight_status || "").toUpperCase();
  if (!PRE_FLIGHT_STATUSES.includes(status)) return 5 * 60 * 1000;

  const depMs = new Date(dbFlight.departure_date).getTime();
  const minsToDep = (depMs - Date.now()) / 60000;

  if (minsToDep > 45) return 0;                 // signal: schedule single timeout to T-45
  if (minsToDep > 15) return 15 * 60 * 1000;    // 45–15 min before: 15 min cadence
  if (minsToDep > 0) return 5 * 60 * 1000;      // < 15 min before: 5 min cadence
  if (status === "DELAYED") return 2 * 60 * 1000; // past departure +2h+: 2 min
  return 60 * 1000;                              // past departure (READY etc.): 1 min
}

// Haversine distance in km
function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Dead reckoning: project position forward by `seconds` at given speed/heading
// Uses great-circle (rhumb-line approximation is sufficient for short intervals)
function projectPosition(lat, lng, speedKmh, headingDeg, seconds) {
  if (!speedKmh || speedKmh <= 0 || !seconds) return { lat, lng };
  const R = 6371;
  const d = (speedKmh * seconds) / 3600 / R; // angular distance in radians
  const bearing = (headingDeg * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lon1 = (lng * Math.PI) / 180;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(bearing)
  );
  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(bearing) * Math.sin(d) * Math.cos(lat1),
      Math.cos(d) - Math.sin(lat1) * Math.sin(lat2)
    );
  return {
    lat: (lat2 * 180) / Math.PI,
    lng: (((lon2 * 180) / Math.PI) + 540) % 360 - 180, // normalise to [-180, 180]
  };
}

function FlightTracker({ dbFlight, tailNumber, onFlightData }) {
  const [searchParams] = useSearchParams();
  const trackingToken = searchParams.get("token");
  const queryClient = useQueryClient();
  const [position, setPosition] = useState(null);
  const [heading, setHeading] = useState(0);
  const [groundMode, setGroundMode] = useState(false); // true when last known alt < 1000 ft
  const [routeData, setRouteData] = useState({ trackCoords: [], origCoord: null, destCoord: null });
  const mapRef = useRef(null);
  const lastPosRef = useRef(null);
  const trackFetchedRef = useRef(false);
  const flyingRef = useRef(false);
  const routeDataRef = useRef({ origCoord: null, destCoord: null });
  const initialZoomedRef = useRef(false);

  // Refs read inside timer callbacks to avoid stale closures
  const dbFlightRef = useRef(dbFlight);
  useEffect(() => { dbFlightRef.current = dbFlight; }, [dbFlight]);
  const hasLandedRef = useRef(false);

  // Adaptive polling
  const pollTimeoutRef = useRef(null);

  // Animation refs (shared between the 2s ticker and the fetch logic)
  const deadReckonRef = useRef(null);   // { lat, lng, speedKmh, headingDeg, lastTime }
  const correctionRef = useRef(null);   // { fromLat, fromLng, toLat, toLng, startTime } | null
  const displayPosRef = useRef(null);   // last position set on the map

  // ─── 2-second animation ticker ────────────────────────────────────────────
  // Between API polls the pin moves every 2 s via dead reckoning.
  // When a correction arrives it glides smoothly over 6 s instead of jumping.
  // The pin freezes within 2 km of the destination to prevent overshoot.
  useEffect(() => {
    const CORRECTION_GLIDE_MS = 6000;
    const OVERSHOOT_FREEZE_KM = 2;

    const tick = () => {
      const now = Date.now();
      let newPos = null;

      if (correctionRef.current) {
        // Smooth glide to corrected API position
        const { fromLat, fromLng, toLat, toLng, startTime } = correctionRef.current;
        const elapsed = now - startTime;

        if (elapsed >= CORRECTION_GLIDE_MS) {
          // Glide complete — resume dead reckoning from corrected position
          newPos = { lat: toLat, lng: toLng };
          correctionRef.current = null;
          if (deadReckonRef.current) {
            deadReckonRef.current.lat = toLat;
            deadReckonRef.current.lng = toLng;
            deadReckonRef.current.lastTime = now;
          }
        } else {
          const t = elapsed / CORRECTION_GLIDE_MS;
          newPos = {
            lat: fromLat + (toLat - fromLat) * t,
            lng: fromLng + (toLng - fromLng) * t,
          };
        }
      } else if (deadReckonRef.current) {
        // Dead reckoning
        const dr = deadReckonRef.current;
        const dt = (now - dr.lastTime) / 1000; // seconds since last tick
        const dest = routeDataRef.current.destCoord;

        // Overshoot prevention: freeze pin once it's within 2 km of destination
        if (dest) {
          const distToDest = haversine(dr.lat, dr.lng, dest[0], dest[1]);
          if (distToDest <= OVERSHOOT_FREEZE_KM) {
            newPos = { lat: dr.lat, lng: dr.lng };
          } else {
            newPos = projectPosition(dr.lat, dr.lng, dr.speedKmh, dr.headingDeg, dt);
            dr.lat = newPos.lat;
            dr.lng = newPos.lng;
            dr.lastTime = now;
          }
        } else {
          newPos = projectPosition(dr.lat, dr.lng, dr.speedKmh, dr.headingDeg, dt);
          dr.lat = newPos.lat;
          dr.lng = newPos.lng;
          dr.lastTime = now;
        }
      }

      if (newPos) {
        displayPosRef.current = newPos;
        setPosition(newPos);
      }
    };

    const animInterval = setInterval(tick, 2000);
    return () => clearInterval(animInterval);
  }, []); // runs once — uses only refs, no stale closures

  // ─── Initial map zoom ──────────────────────────────────────────────────────
  // Fires once per tailNumber when origin + destination coords first arrive.
  // IN_TRANSIT with a live position → fit plane + destination.
  // Otherwise (ground statuses, or IN_TRANSIT before first fix) → fit orig + dest.
  useEffect(() => {
    if (initialZoomedRef.current || !mapRef.current) return;
    const { origCoord, destCoord } = routeData;
    if (!origCoord || !destCoord) return;
    const status = (dbFlight?.flight_status || "").toUpperCase();
    const bounds =
      status === "IN_TRANSIT" && position
        ? [[position.lat, position.lng], destCoord]
        : [origCoord, destCoord];
    initialZoomedRef.current = true;
    flyingRef.current = true;
    mapRef.current.once("moveend", () => { flyingRef.current = false; });
    mapRef.current.fitBounds(bounds, { padding: [40, 40] });
  }, [routeData, position, dbFlight?.flight_status]);

  // ─── Adaptive polling ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!tailNumber) return;

    setPosition(null);
    setRouteData({ trackCoords: [], origCoord: null, destCoord: null });
    setHeading(0);
    setGroundMode(false);
    trackFetchedRef.current = false;
    lastPosRef.current = null;
    deadReckonRef.current = null;
    correctionRef.current = null;
    displayPosRef.current = null;
    hasLandedRef.current = false;
    initialZoomedRef.current = false;

    let mounted = true;

    function scheduleNext(etaMinutes) {
      if (!mounted) return;
      clearTimeout(pollTimeoutRef.current);

      // IN_TRANSIT (or unknown with live position): ETA-based polling
      if (etaMinutes !== null && etaMinutes !== undefined) {
        pollTimeoutRef.current = setTimeout(fetchFlight, getPollingInterval(etaMinutes));
        return;
      }

      // Ground statuses: time-aware tiers based on departure_date
      const flight = dbFlightRef.current;
      const groundInterval = getGroundPollingInterval(flight);
      if (groundInterval === 0 && flight?.departure_date) {
        // > 45 min before departure: schedule one wakeup at T-45min (no tight loop)
        const depMs = new Date(flight.departure_date).getTime();
        const msUntilT45 = Math.max(0, depMs - 45 * 60 * 1000 - Date.now());
        pollTimeoutRef.current = setTimeout(fetchFlight, msUntilT45);
        return;
      }
      pollTimeoutRef.current = setTimeout(fetchFlight, groundInterval);
    }

    async function fetchFlight() {
      if (!mounted) return;
      try {
        // --- Backend proxy version ---
        console.log('[FlightTracker] FR24 poll', { tailNumber });
        const response = await apiClient.getLiveFlightPositions(tailNumber, trackingToken ? { tracking_token: trackingToken } : {});
        const flights = response?.data || [];
        console.log('[FlightTracker] FR24 response', { count: flights.length, dbStatus: dbFlightRef.current?.flight_status });

        if (!mounted) return;

        if (!flights.length) {
          // Empty FR24 response. If the flight was IN_TRANSIT, the aircraft may
          // have landed — confirm via flight-summary and cascade COMPLETED/DIVERTED.
          const currentFlight = dbFlightRef.current;
          const currentStatus = (currentFlight?.flight_status || "").toUpperCase();
          const currentFlightId = currentFlight?.flight_id || currentFlight?.id;
          if (currentStatus === "IN_TRANSIT" && !hasLandedRef.current && currentFlightId) {
            hasLandedRef.current = true;
            try {
              const dep = currentFlight.departure_date;
              if (dep) {
                const depDate = new Date(dep);
                const fromDate = new Date(depDate); fromDate.setDate(fromDate.getDate() - 1);
                const toDate = new Date(depDate); toDate.setDate(toDate.getDate() + 2);
                const stripMs = (iso) => iso.replace(/\.\d{3}Z$/, "Z");
                const summaryData = await apiClient.getFlightSummary(
                  tailNumber,
                  stripMs(fromDate.toISOString()),
                  stripMs(toDate.toISOString()),
                );
                if (!mounted) return;
                const summaries = Array.isArray(summaryData) ? summaryData : (summaryData?.data || []);
                const reg = tailNumber.trim().toUpperCase();
                const landed = summaries.find(
                  (s) => s.flight_ended && (s.reg || "").trim().toUpperCase() === reg,
                );
                if (landed) {
                  const newStatus = landed.dest_icao_actual && landed.dest_icao_actual !== landed.dest_icao
                    ? "DIVERTED"
                    : "COMPLETED";
                  const flightId = currentFlightId;
                  console.log(`[FlightTracker] Landing detected — PATCH ${flightId} → ${newStatus}`);

                  // Optimistic UI flip — update the cached flight_status now
                  // so the badge updates immediately without waiting on the PATCH.
                  queryClient.setQueryData(['flightDetails', flightId], (old) => {
                    if (!old) return old;
                    const apply = (f) =>
                      (f?.flight_id === flightId || f?.id === flightId)
                        ? { ...f, flight_status: newStatus }
                        : f;
                    return Array.isArray(old) ? old.map(apply) : apply(old);
                  });

                  await apiClient.patchFlight(flightId, { flight_status: newStatus });
                  const quoteId = currentFlight.quote_id || currentFlight.quote?.quote_id || currentFlight.quote?.id;
                  if (quoteId) {
                    const newQuoteStatus = await deriveQuoteStatus(quoteId, flightId, newStatus);
                    if (newQuoteStatus) {
                      await apiClient.updateQuote(quoteId, { status: newQuoteStatus });
                    }
                  }
                  // Background sync for other views (list, quotes)
                  queryClient.invalidateQueries({ queryKey: ['flights'] });
                  queryClient.invalidateQueries({ queryKey: ['quotes'] });
                  // Stop polling — terminal status reached
                  return;
                } else {
                  // Not yet landed; allow retry on next empty poll
                  hasLandedRef.current = false;
                }
              } else {
                hasLandedRef.current = false;
              }
            } catch (err) {
              console.error("[FlightTracker] Landing detection failed:", err);
              hasLandedRef.current = false;
            }
          }
          scheduleNext(null);
          return;
        }

        const flight = flights[0];
        const newPos = { lng: flight.lon, lat: flight.lat };
        const newHeading = flight.track || 0;
        // FR24 gspeed is in knots → convert to km/h for dead reckoning
        const speedKmh = (flight.gspeed || 0) * 1.852;
        const alt = flight.alt ?? 0;
        const isGround = alt < 1000;
        setGroundMode(isGround);

        const last = lastPosRef.current;
        const moved = !last || last.lng !== newPos.lng || last.lat !== newPos.lat;
        const rotated = !last || last.heading !== newHeading;

        if (moved) {
          // First fix: set the position. Initial map zoom is handled by the
          // fitBounds effect (which picks plane+dest for IN_TRANSIT, orig+dest
          // for ground statuses). Fall back to a single-point flyTo only when
          // we already have a first fix but no airport coords loaded yet.
          if (!last && mapRef.current) {
            displayPosRef.current = newPos;
            setPosition(newPos);
            if (isGround) {
              // Defer to fitBounds effect once airports load.
            } else if (!routeDataRef.current.destCoord) {
              flyingRef.current = true;
              mapRef.current.once("moveend", () => { flyingRef.current = false; });
              mapRef.current.flyTo([newPos.lat, newPos.lng], 6, { duration: 1 });
            }
          } else if (displayPosRef.current) {
            // Subsequent fix: start a smooth 6-second glide to the corrected position
            correctionRef.current = {
              fromLat: displayPosRef.current.lat,
              fromLng: displayPosRef.current.lng,
              toLat: newPos.lat,
              toLng: newPos.lng,
              startTime: Date.now(),
            };
          }

          // (Re-)initialise dead reckoning from the authoritative API position
          deadReckonRef.current = {
            lat: newPos.lat,
            lng: newPos.lng,
            speedKmh,
            headingDeg: newHeading,
            lastTime: Date.now(),
          };

          // Extend the flown-path polyline with each new authoritative fix.
          // Skip on the very first fix — the FR24 flight-tracks call below
          // will set the historical trail anyway.
          if (last) {
            setRouteData((prev) => {
              const tail = prev.trackCoords[prev.trackCoords.length - 1];
              if (tail && Math.abs(tail[0] - newPos.lat) < 1e-6 && Math.abs(tail[1] - newPos.lng) < 1e-6) {
                return prev;
              }
              return { ...prev, trackCoords: [...prev.trackCoords, [newPos.lat, newPos.lng]] };
            });
          }
        } else if (deadReckonRef.current) {
          // Position unchanged — update speed/heading so dead reckoning stays accurate
          deadReckonRef.current.speedKmh = speedKmh;
          deadReckonRef.current.headingDeg = newHeading;
        }

        // --- FR24 (flight tracks + airport coords) — fetched once ---
        if (!trackFetchedRef.current && flight.fr24_id) {
          trackFetchedRef.current = true;
          const origCode = flight.orig_icao || flight.orig_iata;
          const destCode = flight.dest_icao || flight.dest_iata;

          // --- Backend proxy version ---
          const tokenParams = trackingToken ? { tracking_token: trackingToken } : {};
          const [tracksResult, origResult, destResult] = await Promise.allSettled([
            apiClient.getFlightTracks(flight.fr24_id, tokenParams),
            origCode ? apiClient.getAirportFull(origCode, tokenParams) : Promise.reject(),
            destCode ? apiClient.getAirportFull(destCode, tokenParams) : Promise.reject(),
          ]);

          if (!mounted) return;

          const tracksData = tracksResult.status === "fulfilled" ? tracksResult.value : null;
          const trackPoints = Array.isArray(tracksData)
            ? tracksData[0]?.tracks || []
            : tracksData?.data?.[0]?.tracks || [];
          const origCoord = origResult.status === "fulfilled" ? [origResult.value.lat, origResult.value.lon] : null;
          const destCoord = destResult.status === "fulfilled" ? [destResult.value.lat, destResult.value.lon] : null;

          routeDataRef.current = { origCoord, destCoord };
          setRouteData({
            trackCoords: trackPoints.map((pt) => [pt.lat, pt.lon]),
            origCoord,
            destCoord,
          });
        }

        if (rotated) setHeading(newHeading);

        const isFirst = !last;
        lastPosRef.current = { lng: newPos.lng, lat: newPos.lat, heading: newHeading };

        // Compute progress and distances from coordinates (use ref to avoid stale closure)
        const orig = routeDataRef.current.origCoord;
        const dest = routeDataRef.current.destCoord;
        let progress = 0, distFlown = null, distRemaining = null;
        if (orig && dest) {
          const totalDist = haversine(orig[0], orig[1], dest[0], dest[1]);
          distFlown = haversine(orig[0], orig[1], newPos.lat, newPos.lng);
          distRemaining = Math.max(0, totalDist - distFlown);
          progress = totalDist > 0 ? Math.min((distFlown / totalDist) * 100, 100) : 0;
        }

        if (onFlightData && (isFirst || moved || rotated)) {
          console.log('[FlightTracker] onFlightData', {
            reg: flight.reg, alt: flight.alt, gspeed: flight.gspeed, vspeed: flight.vspeed,
            orig_icao: flight.orig_icao, orig_iata: flight.orig_iata,
            dest_icao: flight.dest_icao, dest_iata: flight.dest_iata,
            isFirst, moved, rotated,
          });
          onFlightData({ ...flight, progress, distFlown, distRemaining });
        }

        // ── Adaptive interval: derive ETA remaining in minutes ──
        let etaMinutes = null;
        if (flight.eta) {
          // eta may be Unix seconds (< 1e12) or ms / ISO string
          const etaMs =
            typeof flight.eta === "number" && flight.eta < 1e12
              ? flight.eta * 1000
              : new Date(flight.eta).getTime();
          const nowMs =
            flight.timestamp
              ? typeof flight.timestamp === "number" && flight.timestamp < 1e12
                ? flight.timestamp * 1000
                : new Date(flight.timestamp).getTime()
              : Date.now();
          etaMinutes = Math.max(0, (etaMs - nowMs) / 60000);
        }

        scheduleNext(etaMinutes);
      } catch (err) {
        console.error(err);
        scheduleNext(null); // fallback to default 5-minute interval
      }
    }

    fetchFlight();

    return () => {
      mounted = false;
      clearTimeout(pollTimeoutRef.current);
    };
    // Only tear down on aircraft / flight identity change. Status updates
    // are read from dbFlightRef inside the polling callbacks, so a PATCH +
    // query refetch should not restart polling (which would blank the map).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tailNumber, dbFlight?.flight_id || dbFlight?.id]);

  // Leaflet marker icons
  const departureIcon = useMemo(() => L.divIcon({
    html: renderToString(<RiFlightTakeoffFill size={24} color="#0a3560" />),
    className: "",
    iconSize: [24, 24],
    iconAnchor: [12, 24],
  }), []);

  const arrivalIcon = useMemo(() => L.divIcon({
    html: renderToString(<RiFlightLandFill size={24} color="#8797a7" />),
    className: "",
    iconSize: [24, 24],
    iconAnchor: [12, 24],
  }), []);

  const planeIcon = useMemo(() => {
    if (groundMode) {
      return L.divIcon({
        html: renderToString(<RiMapPin2Fill size={28} color="#F6B51E" />),
        className: "",
        iconSize: [28, 28],
        iconAnchor: [14, 28],
      });
    }
    return L.divIcon({
      html: renderToString(
        <div style={{ transform: `rotate(${heading}deg)` }}>
          <RiPlaneFill size={34} color="#006fdd" />
        </div>
      ),
      className: "",
      iconSize: [34, 34],
      iconAnchor: [17, 17],
    });
  }, [heading, groundMode]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <MapContainer
        ref={mapRef}
        center={INITIAL_CENTER}
        zoom={INITIAL_ZOOM}
        style={{ width: "100%", height: "100%" }}
        attributionControl={false}
        zoomControl={false}
        maxZoom={18}
        minZoom={2}
        worldCopyJump={false}
      >
        <TileLayer
          url={CARTO_TILES}
          attribution={CARTO_ATTRIBUTION}
        />

        {/* Flight track (solid line) — historical points from FR24 + each poll */}
        {routeData.trackCoords.length >= 2 && (
          <Polyline
            positions={routeData.trackCoords}
            pathOptions={{ color: "#0a3560", weight: 2.5 }}
          />
        )}

        {/* Live segment: from the last polled point to the dead-reckoned pin.
            Keeps the polyline visually attached to the moving aircraft
            between polls (which can be up to 5 min apart at cruise). */}
        {position && routeData.trackCoords.length >= 1 && (
          <Polyline
            positions={[
              routeData.trackCoords[routeData.trackCoords.length - 1],
              [position.lat, position.lng],
            ]}
            pathOptions={{ color: "#0a3560", weight: 2.5 }}
          />
        )}

        {/* Remaining route (dashed line) */}
        {position && routeData.destCoord && (
          <Polyline
            positions={[[position.lat, position.lng], routeData.destCoord]}
            pathOptions={{ color: "#0a3560", weight: 2, dashArray: "8 8", opacity: 0.4 }}
          />
        )}

        {/* Departure pin */}
        {routeData.origCoord && (
          <Marker position={routeData.origCoord} icon={departureIcon} />
        )}

        {/* Arrival pin */}
        {routeData.destCoord && (
          <Marker position={routeData.destCoord} icon={arrivalIcon} />
        )}

        {/* Current position */}
        {position && (
          <Marker position={[position.lat, position.lng]} icon={planeIcon} />
        )}
      </MapContainer>
    </div>
  );
}

export default React.memo(FlightTracker);
